import { useCallback, useEffect, useState } from 'react';
import { Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { BellOutlined, CloseOutlined } from '@ant-design/icons';

import { enablePush, getPermission, isPushConfigured, isPushSupported, onForegroundMessage, registerDevice } from 'lib/push';
import { notify } from 'utils/notify';

/**
 * Push lifecycle + first-run opt-in for the super-admin panel. Renders the
 * opt-in card and nothing else.
 *
 * Mounted inside AuthGuard so every call it makes carries a live public-schema
 * super-admin session; the device endpoints are gated by
 * IsPublicSchemaSuperUser and would 403 on the login screen.
 *
 * The opt-in card is not decoration. `registerDevice` deliberately never
 * prompts — it only refreshes an already-granted device — so without a prompt
 * somewhere, a super-admin is never asked, no token ever reaches the backend,
 * and platform alerts fan out to zero devices while every other part of the
 * system looks healthy. The prompt must come from a user gesture: browsers
 * ignore unprompted requestPermission() calls and Chrome penalises the origin
 * for asking.
 */

const DISMISS_KEY = 'push:prompt:dismissedUntil';
const DISMISS_DAYS = 14;
const APPEAR_DELAY_MS = 4000;

const dismissedUntil = () => {
  try {
    return Number(localStorage.getItem(DISMISS_KEY) || 0) || 0;
  } catch {
    return 0;
  }
};

const dismissFor = (days) => {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + days * 24 * 60 * 60 * 1000));
  } catch {
    /* private mode — the prompt reappearing next session is acceptable */
  }
};

const PushBootstrap = () => {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  /* Device refresh on every app start. Not just first-run setup: the POST
     bumps last_seen_at and carries any FCM token rotation under the unchanged
     device_id. No-ops unless permission is already granted. */
  useEffect(() => {
    registerDevice();
  }, []);

  /* Should we offer the opt-in? "default" is the only actionable permission
     state — granted is already handled above, denied cannot be re-prompted
     from script, unsupported has nothing to offer. */
  useEffect(() => {
    let cancelled = false;
    let timer = null;

    (async () => {
      if (!isPushConfigured() || !(await isPushSupported())) return;
      if (getPermission() !== 'default') return;
      if (Date.now() < dismissedUntil()) return;
      if (cancelled) return;
      timer = setTimeout(() => setVisible(true), APPEAR_DELAY_MS);
    })();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  /* Foreground pushes. FCM does not raise a system notification while the tab
     is focused, so nothing reaches the operator unless we surface it here.
     Unlike the agent panel there is no WebSocket carrying the same event, so
     this is the only foreground channel. */
  useEffect(() => {
    const unsubscribe = onForegroundMessage((payload) => {
      const data = payload.data || {};
      const title = (payload.notification && payload.notification.title) || data.title || 'Platform alert';
      const body = (payload.notification && payload.notification.body) || data.body || '';
      const message = body ? `${title} — ${body}` : title;

      if (data.severity === 'critical' || data.severity === 'error') notify.error(message);
      else if (data.severity === 'warning') notify.warn(message);
      else notify.info(message);
    });

    return unsubscribe;
  }, []);

  const handleEnable = useCallback(async () => {
    setBusy(true);
    try {
      const outcome = await enablePush();
      switch (outcome.status) {
        case 'registered':
          notify.success('Platform alerts enabled on this device.');
          break;
        case 'denied':
          // Permanent until changed in site settings, so re-offering is noise.
          notify.warn('Notifications are blocked. Allow them in your browser site settings, then try again.');
          dismissFor(365);
          break;
        case 'no-token':
          notify.error("Couldn't get a push token from Firebase. Check the browser console.");
          break;
        case 'unsupported':
          notify.warn("This browser can't receive push notifications.");
          break;
        default:
          notify.error("Couldn't enable notifications on this device.");
      }
      if (outcome.status !== 'error') setVisible(false);
    } finally {
      setBusy(false);
    }
  }, []);

  const handleDismiss = useCallback(() => {
    dismissFor(DISMISS_DAYS);
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <Card
      role="dialog"
      aria-label="Enable platform alerts"
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1400,
        width: 'min(360px, calc(100vw - 48px))',
        boxShadow: 6
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <BellOutlined style={{ fontSize: 18, marginTop: 2 }} />
          <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1">Turn on platform alerts</Typography>
            <Typography variant="body2" color="text.secondary">
              Get notified about credential failures, provisioning errors and incidents spreading across workspaces — even when this
              tab is closed.
            </Typography>
          </Stack>
          <Button size="small" onClick={handleDismiss} aria-label="Dismiss" sx={{ minWidth: 32, p: 0.5 }}>
            <CloseOutlined />
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button size="small" color="secondary" onClick={handleDismiss} disabled={busy}>
            Not now
          </Button>
          <Button size="small" variant="contained" onClick={handleEnable} disabled={busy}>
            Turn on
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PushBootstrap;
